import type { ComponentConfigV2, WarmupOptions, WarmupPolicyMode } from '@/types/configurationV2'
import {
  clearWarmupPolicy,
  enableWarmupPolicy,
  resolveWarmupPolicy,
  setWarmupPolicyMode,
  WARMUP_POLICY_MODE_LABELS,
  WARMUP_POLICY_MODES,
  type WarmupRound,
} from '../warmupPolicy'

const ROUND_META: { round: WarmupRound; label: string; help: string }[] = [
  { round: 'initial', label: '첫 학습', help: '오늘 수업의 최초 진도' },
  { round: 'designatedReview', label: '지정 복습', help: '오늘 수업의 강사 지정 복습' },
]

export default function WarmupOptionsEditor({
  component,
  onUpdate,
}: { component: ComponentConfigV2; onUpdate: (u: ComponentConfigV2) => void }) {
  const options = component.options as WarmupOptions
  const update = (audioResponse: WarmupOptions['audioResponse']) =>
    onUpdate({ ...component, options: { ...options, audioResponse } })
  const explicit = component.warmupPolicy != null

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        생각열기/포인트 본문은 깨처 콘텐츠의 <code>warmupText</code> / <code>shortPoint</code> 에서 자동 노출됩니다.
        둘 다 비어 있으면 학습자에게는 자동 생략되고 코칭 화면에 콘텐츠 누락으로 표시됩니다.
      </p>

      <fieldset className="space-y-2 rounded-md border border-gray-200 p-3">
        <legend className="px-1 text-xs font-semibold text-gray-700">회차별 제공 정책</legend>
        <label className="flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={explicit}
            onChange={(e) => onUpdate(e.target.checked ? enableWarmupPolicy(component) : clearWarmupPolicy(component))}
          />
          첫 학습 / 지정 복습 정책 분리
        </label>
        {explicit ? (
          <div className="grid grid-cols-2 gap-3">
            {ROUND_META.map(({ round, label, help }) => (
              <label key={round} className="block text-xs">
                <span className="block font-medium text-gray-700">{label}</span>
                <span className="block text-[11px] text-gray-400">{help}</span>
                <select
                  aria-label={`${label} 생각열기 정책`}
                  value={component.warmupPolicy?.[round] ?? 'required'}
                  onChange={(e) => onUpdate(setWarmupPolicyMode(component, round, e.target.value as WarmupPolicyMode))}
                  className="mt-1 block w-full rounded border border-gray-300 px-2 py-1 text-xs"
                >
                  {WARMUP_POLICY_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {WARMUP_POLICY_MODE_LABELS[mode]}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-gray-500">
            기존 설정 사용 — 첫 학습 {WARMUP_POLICY_MODE_LABELS[resolveWarmupPolicy(component, 'initial').mode]},
            지정 복습 {WARMUP_POLICY_MODE_LABELS[resolveWarmupPolicy(component, 'designatedReview').mode]}
            (1회차에만 제공 체크로 해석).
          </p>
        )}
        <ul className="list-disc space-y-0.5 pl-4 text-[11px] text-gray-500">
          <li>선택(optional)은 녹음 없이 [건너뛰기]로 다음 단계 진행을 허용하고, 건너뜀은 완료와 구분해 기록됩니다.</li>
          <li>필수(required)는 기존과 같이 [다음]으로만 진행하며 아래 녹음 조건을 따릅니다.</li>
          <li>정책 변경은 다음에 시작되는 복습부터 적용됩니다. 진행 중인 복습에 새 의무를 추가하지 않습니다.</li>
        </ul>
      </fieldset>

      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-700">음성 응답 조건 (정책과 별개)</p>
        <label className="flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={options.audioResponse.enabled}
            onChange={(e) =>
              update({ enabled: e.target.checked, required: e.target.checked ? options.audioResponse.required : false })
            }
          />
          음성 응답 사용 (포인트 낭독 녹음)
        </label>

        <label className="flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={options.audioResponse.required}
            disabled={!options.audioResponse.enabled}
            onChange={(e) => update({ ...options.audioResponse, required: e.target.checked })}
          />
          응답 필수 (녹음해야 [다음] 진행 — 선택 정책의 [건너뛰기]에는 적용되지 않음)
        </label>
      </div>
    </div>
  )
}
